import {useCallback, useEffect, useMemo} from 'react'
import {get} from 'lodash-es'
import {useWatch} from 'react-hook-form'
import {useMutation} from '@apollo/client'
import {useRouter} from 'next/router'
import {INPOST_CARRIER, useCheckout, useCheckoutContext} from 'modules/checkout'
import {SET_SHIPPING_ADDRESSES_ON_CART} from '_app/graphql/mutations/cart/SetShippingAddressOnCart'
import {SET_SHIPPING_METHODS_ON_CART} from '_app/graphql/mutations/cart/SetShippingMethodsOnCart'
import {AvailableShippingMethod, Cart} from '_app/types/api'
import {Point} from 'modules/checkout/types'
import {getDefaultShippingMethodForm} from 'modules/checkout/utils/shippingMethod'
import getLocale from '_app/utils/localized/getLocale'
import EventsHelper from '../../../../../helpers/EventsHelper/EventsHelper'

export const useShippingMethod = (methods: any) => {
  const { locale } = useRouter()
  const {
    setShippingValue,
    setShippingMethodCode,
    cartSummaryData,
    cartCheckoutData: data,
    setInpostAddress,
    setInpostAddressDetails,
  } = useCheckoutContext()

  const [setShippingAddressesOnCart] = useMutation(
    SET_SHIPPING_ADDRESSES_ON_CART
  )
  const [setShippingMethodsOnCart] = useMutation(SET_SHIPPING_METHODS_ON_CART)

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
    getValues,
  } = methods

  const anyOptionsOnProducts = useMemo(() => {
    const cartSummaryDataExists = !!cartSummaryData &&
      !!cartSummaryData.cart &&
      !!cartSummaryData.cart.items

    const foundItem = cartSummaryDataExists && cartSummaryData?.cart?.items?.find((item) => {
      const enteredOptionsExist = item?.entered_options?.option &&
        item?.entered_options?.option?.length > 0 &&
        Array.isArray(item?.entered_options?.option)
      if (enteredOptionsExist) {
        return item?.entered_options?.option?.some((opt) => {
          return ['malowanie', 'grawerunek'].some((type) => {
            return opt?.label?.toLowerCase().includes(type)
          })
        })
      }

      return false
    })

    return !!foundItem
  }, [cartSummaryData])

  const shippingMethodsData =
    data?.cart?.shipping_addresses?.[0]?.available_shipping_methods || []

  const selectedShippingMethod = useWatch({
    control,
    name: 'shipping_addresses[0].selected_shipping_method',
  })

  const checkedShipping = useWatch({
    control,
    name: 'shipping_addresses[0].selected_shipping_method.carrier_code',
  })

  const availableShipping: any = get(
    data?.cart,
    'shipping_addresses[0].available_shipping_methods'
  )

  const availableShippingMethods = useMemo(() => {
    if (anyOptionsOnProducts) {
      return shippingMethodsData?.filter(el => {
        return el?.carrier_code !== 'flatrate2'
      })
    }
    return shippingMethodsData
  }, [anyOptionsOnProducts, shippingMethodsData])

  const errorMessageInpost = get(
    errors,
    'shipping_addresses[0].selected_shipping_method.paczkomaty_target_point'
  )

  const onSubmit = useCallback(async (data: Cart) => {
      const carrierCode = get(
        data,
        'shipping_addresses[0].selected_shipping_method.carrier_code'
      )

      const availableShippingMethods: any = get(
        data,
        'shipping_addresses[0].available_shipping_methods'
      )

      const variables = {
        shippingMethods: [
          {
            carrier_code: carrierCode,
            method_code: availableShippingMethods?.length > 0 ? availableShippingMethods?.find(
              (sm: AvailableShippingMethod) => sm?.carrier_code === carrierCode
            )?.method_code : carrierCode,
            ...(carrierCode === INPOST_CARRIER
              ? {
                paczkomaty_target_point: get(
                  data,
                  'shipping_addresses[0].selected_shipping_method.paczkomaty_target_point'
                ),
                showroom_target: null,
              }
              : {}),
            ...(carrierCode === 'pickup'
              ? {
                showroom_target: get(
                  data,
                  'shipping_addresses[0].selected_shipping_method.showroom_target'
                ),
                paczkomaty_target_point: null,
              }
              : {}),
          },
        ],
      }

      let request: any = null
      if ((data?.shipping_addresses?.[0]?.country?.code === 'PL' && data?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code !== 'tablerate') || (data?.shipping_addresses?.[0]?.country?.code !== 'PL' && data?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code === 'tablerate')) {
        request = await setShippingMethodsOnCart({
          variables,
          refetchQueries: ['GetCart'],
        })
      }
      return request
    },
    [setShippingMethodsOnCart, data]
  )

  useEffect(() => {
    const shippingPrice = availableShipping?.find(
      (method: AvailableShippingMethod) =>
        method.carrier_code === checkedShipping
    )?.amount?.value

    if (shippingPrice !== undefined) {
      setShippingValue(shippingPrice)
    }
  }, [availableShipping, checkedShipping, setShippingValue])
  useEffect(() => {
    setShippingMethodCode(selectedShippingMethod?.method_code)
  }, [selectedShippingMethod?.method_code, setShippingMethodCode])

  useEffect(() => {
    if (selectedShippingMethod?.carrier_code && availableShippingMethods.length > 0) {
      const values = getValues()
      onSubmit(values)
    }
  }, [
    selectedShippingMethod?.carrier_code,
    availableShippingMethods,
    setValue,
    getValues,
    handleSubmit,
    onSubmit,
  ])

  useEffect(() => {
    if (errorMessageInpost) {
      EventsHelper.scrollToElement('errorMessageInpost')
    } else {
      EventsHelper.scrollToElement('ShippingMethodForm')
    }
  }, [errorMessageInpost, errors])

  useCheckout({
    setShippingAddressesOnCart,
    control,
    data,
    defaultValue: get(
      getValues() || data?.cart,
      'shipping_addresses[0].country.code',
      getLocale(locale).toUpperCase()
    ),
  })

  useEffect(() => {
    if (typeof window !== undefined) {
      (window as any).easyPackAsyncInit = function () {
        (window as any).easyPack?.init({})
      }
    }
  }, [])

  useEffect(() => {
    if (data?.cart) {
      const clone = getDefaultShippingMethodForm(data)
      reset(clone)
    }
  }, [data?.cart, reset])

  useEffect(() => {
    const parcelId = selectedShippingMethod?.paczkomaty_target_point
    if (checkedShipping === INPOST_CARRIER && !!parcelId) {
      (window as any).easyPack?.points.find(parcelId, function (point: Point) {
        setInpostAddress(`${point.address.line1} ${point.address.line2}`)
        setInpostAddressDetails(point?.address_details)
      })
    }
  }, [
    checkedShipping,
    selectedShippingMethod?.paczkomaty_target_point,
    setInpostAddress,
    setInpostAddressDetails,
  ])

  return {
    availableShippingMethods,
    selectedShippingMethod,
    handleSubmit: (values: any) => handleSubmit(onSubmit)(values),
    errorMessageInpost,
  }
}
